import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr, API } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import {
    ShoppingCart, ReceiptX, HandCoins, Bank, DownloadSimple,
    FileXls, ArrowsClockwise, Package, ShieldCheck,
} from "@phosphor-icons/react";

/**
 * Reports for CA — one page, month picker, four downloadable Excel
 * reports plus a "bundle everything" mega workbook. Founder + accountant
 * only. Backend enforces the role via require_roles.
 */

function defaultMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportsCA() {
    const [month, setMonth] = useState(defaultMonth());
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(null); // key of active download

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/reports-ca/summary?month=${month}`);
            setSummary(data);
        } catch (e) { toast.error(formatApiError(e)); setSummary(null); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, [month]);  // eslint-disable-line react-hooks/exhaustive-deps

    const download = async (key, filename) => {
        setDownloading(key);
        try {
            const url = `${API}/reports-ca/${key}.xlsx?month=${month}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success(`${filename} downloaded`);
        } catch (e) { toast.error(e.message || "Download failed"); }
        finally { setDownloading(null); }
    };

    const monthLabel = useMemo(() => {
        try {
            const d = new Date(month + "-01");
            return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
        } catch { return month; }
    }, [month]);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Reports for CA"
                sub="Monthly export bundle ready for GST and TDS filings. Attach the workbook, hit send — your CA has everything they need."
            />

            <div className="p-8 space-y-6">
                {/* Month picker */}
                <div className="bg-white border rounded-lg p-4 flex items-end gap-4"
                     style={{ borderColor: "var(--se-border)" }}>
                    <div>
                        <Label>Month</Label>
                        <Input type="month" value={month}
                               onChange={(e) => setMonth(e.target.value)}
                               className="w-52" data-testid="rca-month" />
                    </div>
                    <div className="flex-1">
                        <div className="text-lg font-serif">{monthLabel}</div>
                        <div className="text-xs text-stone-500">All figures pulled live from posted journals — drafts are excluded.</div>
                    </div>
                    <Button variant="outline" onClick={refresh} disabled={loading}
                            data-testid="rca-refresh">
                        <ArrowsClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button
                        onClick={() => download("bundle", `reports-for-ca-${month}.xlsx`)}
                        disabled={downloading === "bundle" || !summary}
                        style={{ background: "var(--se-brand-grad)", color: "#fff" }}
                        data-testid="rca-download-bundle"
                    >
                        <Package size={14} className="mr-2" />
                        {downloading === "bundle" ? "Building…" : "Download all as one workbook"}
                    </Button>
                </div>

                {/* Report cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard
                        icon={ShoppingCart} accent="#0d6efd"
                        title="Purchase register"
                        sub="Every posted vendor invoice this month with party details, GSTIN, taxable, CGST, SGST, IGST, total."
                        stats={summary?.purchase && [
                            { k: "Invoices", v: summary.purchase.count },
                            { k: "Taxable", v: `₹ ${inr(summary.purchase.taxable)}` },
                            { k: "GST", v: `₹ ${inr(summary.purchase.gst)}` },
                            { k: "Total", v: `₹ ${inr(summary.purchase.total)}` },
                        ]}
                        onDownload={() => download("purchase", `purchase-register-${month}.xlsx`)}
                        downloading={downloading === "purchase"}
                        testid="rca-purchase"
                    />
                    <ReportCard
                        icon={ReceiptX} accent="#16a34a"
                        title="Sales register"
                        sub="Every posted customer invoice this month with party details, GSTIN, taxable, CGST, SGST, IGST, total."
                        stats={summary?.sales && [
                            { k: "Invoices", v: summary.sales.count },
                            { k: "Taxable", v: `₹ ${inr(summary.sales.taxable)}` },
                            { k: "GST", v: `₹ ${inr(summary.sales.gst)}` },
                            { k: "Total", v: `₹ ${inr(summary.sales.total)}` },
                        ]}
                        onDownload={() => download("sales", `sales-register-${month}.xlsx`)}
                        downloading={downloading === "sales"}
                        testid="rca-sales"
                    />
                    <ReportCard
                        icon={HandCoins} accent="#0891b2"
                        title="TDS payable"
                        sub="Every TDS deduction posted this month — section-wise, per party, with PAN & GSTIN."
                        stats={summary?.tds && [
                            { k: "Entries", v: summary.tds.count },
                            { k: "Total TDS", v: `₹ ${inr(summary.tds.total)}` },
                            ...Object.entries(summary.tds.by_section || {}).slice(0, 3).map(([sec, amt]) => ({
                                k: sec, v: `₹ ${inr(amt)}`,
                            })),
                        ]}
                        onDownload={() => download("tds", `tds-payable-${month}.xlsx`)}
                        downloading={downloading === "tds"}
                        testid="rca-tds"
                    />
                    <ReportCard
                        icon={ShieldCheck} accent="#7c3aed"
                        title="Statutory payables"
                        sub="GST (output vs input), TDS, PF (Employee + Employer), Professional Tax — closing balances the government still expects for this month."
                        stats={(() => {
                            const s = summary?.statutory?.by_bucket || {};
                            if (!summary?.statutory) return null;
                            const pf   = ((s["PF"]) || { closing: 0 }).closing;
                            const pt   = ((s["PT"]) || { closing: 0 }).closing;
                            const gsto = ((s["GST Output"]) || { closing: 0 }).closing;
                            const tds  = ((s["TDS"]) || { closing: 0 }).closing;
                            return [
                                { k: "GST payable",  v: `₹ ${inr(gsto)}` },
                                { k: "TDS payable",  v: `₹ ${inr(tds)}` },
                                { k: "PF payable",   v: `₹ ${inr(pf)}` },
                                { k: "PT payable",   v: `₹ ${inr(pt)}` },
                            ];
                        })()}
                        onDownload={() => download("statutory", `statutory-payables-${month}.xlsx`)}
                        downloading={downloading === "statutory"}
                        testid="rca-statutory"
                    />
                    <ReportCard
                        icon={Bank} accent="#a855f7"
                        title="Bank & cash movement"
                        sub="Opening balance, every debit + credit with counter-party mapping, closing balance — per bank and per cash ledger."
                        stats={summary?.bank && [
                            { k: "Accounts", v: summary.bank.accounts.length },
                            ...summary.bank.accounts.slice(0, 3).map((a) => ({
                                k: a.name.replace(/ Bank Account$/, ""),
                                v: `${a.inflows}↓ ${a.outflows}↑`,
                            })),
                        ]}
                        onDownload={() => download("bank", `bank-cash-movement-${month}.xlsx`)}
                        downloading={downloading === "bank"}
                        testid="rca-bank"
                    />
                </div>

                {/* Bank accounts detailed strip */}
                {summary?.bank?.accounts?.length ? (
                    <div className="bg-white border rounded-lg overflow-hidden"
                         style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-4 py-3 border-b"
                             style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                            <div className="text-sm font-medium">Bank & cash preview — {monthLabel}</div>
                            <div className="text-[11px] text-stone-500">One row per account. Detailed movement lines are in the downloaded workbook.</div>
                        </div>
                        <table className="se-table" data-testid="rca-bank-preview">
                            <thead><tr>
                                <th>Account</th>
                                <th className="text-right">Opening</th>
                                <th className="text-right">Money in</th>
                                <th className="text-right">Money out</th>
                                <th className="text-right">Closing</th>
                                <th className="text-center">Lines</th>
                            </tr></thead>
                            <tbody>
                                {summary.bank.accounts.map((a) => {
                                    const net = a.opening + a.total_in - a.total_out;
                                    const drift = Math.abs(net - a.closing);
                                    return (
                                        <tr key={a.name}>
                                            <td className="text-sm">
                                                {a.name}
                                                <div className="text-[10px] text-stone-500 capitalize">{a.kind}</div>
                                            </td>
                                            <td className="se-num text-right">₹ {inr(a.opening)}</td>
                                            <td className="se-num text-right text-emerald-700">₹ {inr(a.total_in)}</td>
                                            <td className="se-num text-right text-rose-700">₹ {inr(a.total_out)}</td>
                                            <td className="se-num text-right font-medium">
                                                ₹ {inr(a.closing)}
                                                {drift > 0.02 ? (
                                                    <div className="text-[10px] text-rose-500" title={`Drift: ₹${drift.toFixed(2)}`}>
                                                        drift ₹{drift.toFixed(2)}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="text-center text-xs text-stone-600">
                                                {a.inflows}↓ {a.outflows}↑
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>
        </>
    );
}

function ReportCard({ icon: Icon, accent, title, sub, stats, onDownload, downloading, testid }) {
    return (
        <div className="bg-white border rounded-lg p-5 flex flex-col"
             style={{ borderColor: "var(--se-border)" }}
             data-testid={testid}>
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
                     style={{ background: accent }}>
                    <Icon size={18} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-stone-900">{title}</div>
                    <div className="text-[12px] text-stone-500 leading-snug mt-0.5">{sub}</div>
                </div>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2 my-3">
                {(stats || []).map((s, i) => (
                    <div key={i} className="rounded-md px-3 py-2"
                         style={{ background: "#fafaf9", border: "1px solid var(--se-border)" }}>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500">{s.k}</div>
                        <div className="text-sm font-medium text-stone-900 truncate">{s.v}</div>
                    </div>
                ))}
                {!stats ? (
                    <div className="col-span-2 text-xs text-stone-400 italic">Loading…</div>
                ) : null}
            </div>
            <Button onClick={onDownload} disabled={downloading}
                    className="mt-1"
                    style={{ background: accent, color: "#fff" }}
                    data-testid={`${testid}-btn`}>
                {downloading ? (
                    <>
                        <ArrowsClockwise size={14} className="mr-2 animate-spin" /> Preparing…
                    </>
                ) : (
                    <>
                        <FileXls size={14} className="mr-2" /> Download .xlsx
                    </>
                )}
            </Button>
        </div>
    );
}
